import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Plus, X, Loader2, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PERIODS = [
  { num: 1, start: "09:00", end: "10:00" },
  { num: 2, start: "10:00", end: "11:00" },
  { num: 3, start: "11:00", end: "12:00" },
  { num: 4, start: "13:00", end: "14:00" },
  { num: 5, start: "14:00", end: "15:00" },
  { num: 6, start: "15:00", end: "16:00" },
];

const COURSE_STYLES = {
  "DS301": { bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8", dot: "#3B82F6" },
  "AI402": { bg: "#F5F3FF", border: "#DDD6FE", text: "#5B21B6", dot: "#7C3AED" },
  "DB201": { bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D", dot: "#16A34A" },
  "OS301": { bg: "#FFF7ED", border: "#FED7AA", text: "#C2410C", dot: "#EA580C" },
  "CN401": { bg: "#ECFEFF", border: "#A5F3FC", text: "#0E7490", dot: "#0891B2" },
  "ML501": { bg: "#FFF1F2", border: "#FECDD3", text: "#BE185D", dot: "#DB2777" },
  "DEFAULT": { bg: "#F8FAFC", border: "#E2E8F0", text: "#475569", dot: "#94A3B8" },
};

function SlotCard({ slot, onDelete }) {
  const s = COURSE_STYLES[slot.course_code] || COURSE_STYLES.DEFAULT;
  return (
    <div className="relative h-full p-2.5 border group cursor-default" style={{ background: s.bg, borderColor: s.border }}>
      <div className="flex items-center gap-1 mb-1">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.dot }} />
        <span className="font-['JetBrains_Mono'] text-[9px] font-bold" style={{ color: s.text }}>{slot.course_code}</span>
      </div>
      <p className="font-['Syne'] font-bold text-[10px] leading-tight line-clamp-2" style={{ color: s.text }}>{slot.subject}</p>
      <p className="font-['DM_Sans'] text-[9px] text-[#7A7468] mt-0.5">{slot.room}</p>
      <p className="font-['DM_Sans'] text-[9px] text-[#BDBAA8] truncate">{slot.faculty_name?.split(" ").slice(-1)[0]}</p>
      <button onClick={() => onDelete(slot._id)}
        className="absolute top-1 right-1 w-5 h-5 bg-[#FF3636] text-white items-center justify-center hidden group-hover:flex transition-all" data-testid={`delete-slot-${slot._id}`}>
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function Timetable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [semester, setSemester] = useState(5);
  const [department, setDepartment] = useState("CSE");
  const [showModal, setShowModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [form, setForm] = useState({ subject: "", course_code: "", faculty_name: "", room: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchTimetable(); }, [semester, department]);

  const fetchTimetable = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/timetable?semester=${semester}&department=${department}`, { withCredentials: true });
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  const getSlot = (day, period) => data.find(d => d.day === day && d.period === period);

  const handleAddClick = (day, period) => {
    const p = PERIODS.find(pp => pp.num === period);
    setSelectedCell({ day, period, start_time: p.start, end_time: p.end });
    setError(""); setForm({ subject: "", course_code: "", faculty_name: "", room: "" });
    setShowModal(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      await axios.post(`${API}/timetable`, {
        ...form, day: selectedCell.day, period: selectedCell.period,
        start_time: selectedCell.start_time, end_time: selectedCell.end_time, semester, department
      }, { withCredentials: true });
      setShowModal(false);
      fetchTimetable();
    } catch (err) { setError(err.response?.data?.detail || "Failed to add slot"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/timetable/${id}`, { withCredentials: true });
    fetchTimetable();
  };

  const totalClasses = data.length;
  const uniqueFaculty = [...new Set(data.map(d => d.faculty_name))].length;
  const rooms = [...new Set(data.map(d => d.room))].length;

  return (
    <div className="space-y-6" data-testid="timetable-module">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic Planning</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Weekly Timetable</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8]">Semester</label>
            <select value={semester} onChange={e => setSemester(Number(e.target.value))}
              className="bg-white border border-[#D8D4CB] font-['JetBrains_Mono'] text-sm text-[#0C0C10] px-2.5 py-1.5 focus:outline-none focus:border-[#0C0C10]">
              {[1,2,3,4,5,6,7,8].map(s => <option key={s} value={s}>Sem {s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8]">Dept</label>
            <select value={department} onChange={e => setDepartment(e.target.value)}
              className="bg-white border border-[#D8D4CB] font-['DM_Sans'] text-sm text-[#0C0C10] px-2.5 py-1.5 focus:outline-none focus:border-[#0C0C10]">
              {["CSE", "ECE", "ME", "Civil", "EE", "MBA", "MCA"].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Slots", value: totalClasses, color: "#0A7AFF" },
          { label: "Faculty", value: uniqueFaculty, color: "#8B5CF6" },
          { label: "Rooms / Labs", value: rooms, color: "#22C55E" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#E2DDD4] p-4" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(COURSE_STYLES).filter(([k]) => k !== "DEFAULT").map(([code, s]) => (
          <div key={code} className="flex items-center gap-1.5 px-2 py-1 border text-[9px] font-['JetBrains_Mono'] font-bold" style={{ borderColor: s.border, background: s.bg, color: s.text }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
            {code}
          </div>
        ))}
        <p className="font-['DM_Sans'] text-xs text-[#BDBAA8] flex items-center ml-2">Click any empty cell to add a class slot</p>
      </div>

      {/* Timetable Grid */}
      <div className="overflow-x-auto bg-white border border-[#E2DDD4]">
        <div className="min-w-[800px]">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          ) : (
            <>
              {/* Header row */}
              <div className="grid border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "76px repeat(6, 1fr)" }}>
                <div className="px-3 py-3 border-r border-[#E2DDD4]">
                  <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8]">Period</p>
                </div>
                {DAYS.map(day => (
                  <div key={day} className="px-2 py-3 border-r border-[#E2DDD4] last:border-r-0 text-center">
                    <p className="font-['Syne'] font-bold text-[10px] uppercase tracking-wider text-[#0C0C10]">{day.slice(0, 3).toUpperCase()}</p>
                    <p className="font-['DM_Sans'] text-[9px] text-[#BDBAA8] hidden sm:block">{day}</p>
                  </div>
                ))}
              </div>

              {/* Period rows */}
              {PERIODS.map(period => (
                <div key={period.num} className="grid border-b border-[#E2DDD4] last:border-b-0" style={{ gridTemplateColumns: "76px repeat(6, 1fr)", minHeight: "88px" }}>
                  <div className="px-3 py-3 border-r border-[#E2DDD4] flex flex-col justify-center bg-[#FAFAF7]">
                    <p className="font-['JetBrains_Mono'] font-bold text-sm text-[#0C0C10]">P{period.num}</p>
                    <p className="font-['DM_Sans'] text-[9px] text-[#BDBAA8]">{period.start}</p>
                    <p className="font-['DM_Sans'] text-[9px] text-[#BDBAA8]">{period.end}</p>
                  </div>
                  {DAYS.map(day => {
                    const slot = getSlot(day, period.num);
                    return (
                      <div key={day} className="border-r border-[#E2DDD4] last:border-r-0 p-1">
                        {slot ? (
                          <SlotCard slot={slot} onDelete={handleDelete} />
                        ) : (
                          <button onClick={() => handleAddClick(day, period.num)} data-testid={`cell-${day}-${period.num}`}
                            className="w-full h-full min-h-[72px] flex items-center justify-center text-[#D8D4CB] hover:text-[#0A7AFF] hover:bg-[#EFF6FF] transition-all group border border-dashed border-transparent hover:border-[#BFDBFE]">
                            <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Add Slot Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-2">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Class Slot</h3>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468] mb-6">
                {selectedCell?.day} — Period {selectedCell?.period} ({selectedCell?.start_time}–{selectedCell?.end_time})
              </p>
              {error && <p className="font-['DM_Sans'] text-xs text-[#FF3636] mb-4 p-3 bg-[#FF3636]/8 border border-[#FF3636]/20">{error}</p>}
              <form onSubmit={handleAdd} className="space-y-4">
                {[["subject", "Subject Name"], ["course_code", "Course Code (e.g. DS301)"], ["faculty_name", "Faculty Name"], ["room", "Room / Lab Number"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                    <input value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} required
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <button type="submit" disabled={saving} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add to Timetable"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
